import styled from "styled-components";
import { Box, Flow, FlexBox, } from "./spacial"
import { DEVICE_PIXEL_THRESHOLD } from "@bracket/ui/const";
import { getTheme, } from "@bracket/ui/themes";

import type { CSSSpaceDeclaration, CssSpaceValue, ColorDeclaration, FlexAlignment, Strata, } from "@bracket/ui/types";




/** A Base class for all content containers, providing flex-direction as a stack (vertical) or stream (horizontal), strata (high/medium/low etc) and SELF alignment properties.
 * !! IMPORTANT: by default all streams become stacks when in MOBILE viewports. To opt out of this, property $desktopFlow can be set to "stream".
 * 
 * @prop $strata - the layer to attach this strut to: options are low-high: lowest, low, default, high, highest and two additional layers: secondary and primary.
 * @prop $align - the alignment of the container in the context of self-alignment to parent. options are "flex-start, "flex-end", "center".
 * @prop $flow - the flow of the container: options are "stream" for horizontal and "stack" for vertical.
 * @prop $desktopFlow - the flow of the container on DESKTOP viewports: options are "stream" for horizontal and "stack" for vertical. By default, this is set to "stack" to ensure that all streams become stacks on MOBILE viewports. */
export const Strut = styled(Flow)<{
	$flow?: "stream"|"stack"|"horizontal"|"vertical";
	$desktopFlow?: "stream"|"stack";
	$flexible?: boolean;
	$overflow?: "auto" | "hidden" | "scroll" | "visible";
}>`
	${({ $flow = "stack", $align }) => $align !== undefined ? $flow === "stack" ? `align-self: ${$align};` : `justify-content: ${$align};` : ""}
	${({ $justify, $flow = "stack", }) => $justify !== undefined ? $flow === "stack" ? `justify-self: ${$justify};` : `justify-content: ${$justify};` : ""}
	${({ $overflow }) => $overflow !== undefined ? `overflow: ${$overflow};` : ""}
	@media(min-width: ${DEVICE_PIXEL_THRESHOLD}px) {
		${({ $desktopFlow = "stack", }) => $desktopFlow !== "stack" ? `flex-direction: row;` : ""}
	}
`;



/** A panel is the most common container in the UI system. Extending a strut with padding, margin and spacing properties.*/
export const Panel = styled(Strut)<{
 $margin?: CSSSpaceDeclaration;
 $padding?: CSSSpaceDeclaration;
 $height?: CssSpaceValue;
 $width?: CssSpaceValue;
 $gap?: CSSSpaceDeclaration;
 $bgColor?: ColorDeclaration;
}>`
	${({ $height }) => $height && `min-height: ${$height};`}
	${({ $margin, }) => $margin && `margin: ${$margin};`}
	${({ $padding, }) => $padding && `padding: ${$padding};`}
	${({ $width, }) => $width && `min-width: ${$width};`}
	${({ $gap }) => $gap && `gap: ${$gap};`}
	${({ $bgColor }) => $bgColor && `background-color: ${$bgColor};`}
`;

export const PanelFlex = styled(Panel).attrs({ $flow: "stack", $flexible: true, })``;

export const UIBase = styled(Panel)`
	${({ $debug, }) => $debug ? "background-color: red;" : ""};
	align-items: ${({ $align }) => $align !== undefined ? `${$align};` : "center;"}
	justify-content: ${({ $justify }) => $justify !== undefined ? `${$justify};` : "center;"}
	cursor: pointer;
	padding: ${({ $padding }) => $padding !== undefined ? `${$padding};` : "8px 12px 8px 12px;"};
	margin: ${({ $margin }) => $margin !== undefined ? `${$margin};` : "0rem;"};
	border-radius: 8px;
	${({ $debug, }) => $debug ? "background-color: red;" : ""};
	background-color: ${({ $strata = "highest", }) => `${getTheme().surface_container_highest}`};
	&:hover {
		>.text-node {
			color: ${getTheme().surface_on}!important;
		}
	}
`;

const stratifiedBackground = (strata: Strata) => strata === "default" ? "surface_container" : `surface_container_${strata}`; 

/** A Slate extends a panel with visual elements such as background, border, radius */

export const Slate = styled(Panel)<{
	$strata?: Strata;
	$shadow?: boolean;
    $borderRadius?: CssSpaceValue;
}>`
	background-color: ${({ $strata, }) => `${
        // @ts-expect-error: experimental right now.
        getTheme()[stratifiedBackground($strata)]
    };`};
	${({ $borderRadius, }) => $borderRadius && `border-radius: ${$borderRadius};`}
	${({ $gap, }) => $gap && `gap: ${$gap};`}
	${({ $shadow }) => $shadow && `box-shadow: ${getTheme().shadows.primary}`}
`;





export const Card = styled(Panel)`
    background-color: ${getTheme().surface_container_high};
    border-radius: 0.5rem;
    padding: 1rem;
    margin: 0.5rem;
    gap: 0.5rem;
`;

export const Section = styled(Card)`
	height: 100%;
	width: 100%;
`;



/** @deprecated, replace and remove when time allows. */
export const Fit = styled(FlexBox)<{ $width?: CssSpaceValue; $height?: CssSpaceValue; $justify?: FlexAlignment; $align?: FlexAlignment; $color?: ColorDeclaration; $bgColor?: ColorDeclaration; }>`
	flex: 1;
	flex-direction: row;
	${({ $width }) => $width !== undefined ? `width: ${$width};` : ""}
	${({ $height }) => $height !== undefined ? `height: ${$height};` : ""}
	${({ $justify }) => $justify !== undefined ? `justify-content: ${$justify};` : ""}
	${({ $align }) => $align !== undefined ? `align-items: ${$align};` : ""}
	${({ $bgColor }) => $bgColor !== undefined ? `background-color: ${$bgColor};` : ""}
	${({ $color }) => $color !== undefined ? `color: ${$color};` : ""}
`;

export const Page = styled(Strut).attrs({ $flow: "stack", $flexible: true, })`
	flex-wrap: nowrap;
	color: ${() => getTheme().surface_on};
	font-family: ${() => getTheme().fonts.body};
	font-weight: ${() => getTheme().typeface.p.md.weight};
	margin-top: ${() =>
		typeof window !== "undefined" && globalThis.localStorage.getItem("bracket-banner-test") === "true"
			? "80px"
			: "0px"};
	@media(min-width: 1024px) {
		margin-top: 24px;
		gap: 24px;
	}
`;

export const SurfaceContainer = styled(Box)`
	background-color: ${getTheme().surface_container};
	border-radius: 8px;
`;



export const ErrantSlate = styled(Slate)<{ $dark?: boolean }>`
	background-color: ${() => getTheme().error};
	color: ${() => getTheme().error_on};
`;
